import { useRef, useState } from "react";
// AUD-F: passa a usar o hook de segmento do proprio modulo de Checklist,
// como AvariasPanel, ChecklistForm e os outros ja fazem desde o AUD-C.
import { useTextos } from "../context/useTextos";
import {
  MAX_FOTOS_AVARIA,
  TIPOS_AVARIA,
  GRAVIDADE_AVARIA,
  GRAVIDADE_LABELS,
  FORMAS,
  FORMAS_COM_ARRASTE,
  CORES_MARCACAO,
  ESPESSURAS,
  corDaMarcacao,
  normalizarPonto,
} from "../lib/avariaModel";
import { generateId } from "../lib/id";
import { compressImageFile } from "../lib/imageCompression";

// Marcação de avarias sobre fotos reais do veículo.
// Captura via câmera do dispositivo (capture="environment") ou upload de
// arquivo; cada foto fica disponível para marcação.
// Limitado a MAX_FOTOS_AVARIA fotos para não sobrecarregar o armazenamento.
//
// ── COMO AS MARCAÇÕES SÃO GUARDADAS ────────────────────────────────────────
// Nada é "queimado" no pixel da foto. Cada marcação é um registro com
// coordenadas em PORCENTAGEM da imagem (ver avariaModel.normalizarPonto):
// a foto original nunca é reprocessada, o arquivo não engorda, e a marcação
// continua editável e removível depois. É também o que permite redesenhar
// tudo no PDF sem perder nitidez.
//
// Pinos continuam sendo botões em HTML -- é o que já funcionava e não foi
// tocado. As formas novas (seta, retângulo, elipse, texto) são desenhadas
// numa camada SVG por cima. Esse SVG NÃO tem viewBox de propósito: com
// coordenadas em "%" e espessura em px, nada distorce em foto deitada ou em
// pé, e a ponta da seta não estica.
//
// Marcação salva antes desta tela existir não tem o campo "forma" --
// normalizarPonto a trata como pino, então checklist antigo abre igual.
// AUD-CK1 (21/10/2026) -- geometria de quem nasce por TOQUE.
//
// Um toque da um ponto so, e seta/retangulo/elipse precisam de dois. Estas
// funcoes inventam o segundo de forma previsivel, e tudo em % da imagem,
// como o resto do arquivo.
const TAMANHO_PADRAO = 14; // % da menor dimensao util -- cabe no dedo e nao cobre a foto

// Nome proprio de proposito: o componente ja tem um `limitar` interno
// (linha ~108). Dois nomes iguais no mesmo arquivo confundem leitura e
// escondem erro de escopo, entao este aqui se chama outra coisa.
function limitarPercentual(v) {
  return Math.max(0, Math.min(100, v));
}

function geometriaPadrao(forma, x, y) {
  if (forma === FORMAS.SETA) {
    // A PONTA vai no ponto tocado: o desenho usa markerEnd, que sempre fica
    // em (x2,y2). O rabo nasce acima e a esquerda; perto da borda ele
    // inverte o lado, senao a seta nasceria com comprimento zero.
    const dx = x - TAMANHO_PADRAO >= 0 ? -TAMANHO_PADRAO : TAMANHO_PADRAO;
    const dy = y - TAMANHO_PADRAO >= 0 ? -TAMANHO_PADRAO : TAMANHO_PADRAO;
    return { x: limitarPercentual(x + dx), y: limitarPercentual(y + dy), x2: limitarPercentual(x), y2: limitarPercentual(y) };
  }
  // Retangulo e elipse cercam a avaria: nascem centrados no toque. Perto da
  // borda a caixa DESLIZA para dentro em vez de encolher, para nao virar um
  // risco fino.
  const m = TAMANHO_PADRAO / 2;
  let a = x - m, b = x + m;
  if (a < 0) { a = 0; b = TAMANHO_PADRAO; }
  if (b > 100) { b = 100; a = 100 - TAMANHO_PADRAO; }
  let c = y - m, d = y + m;
  if (c < 0) { c = 0; d = TAMANHO_PADRAO; }
  if (d > 100) { d = 100; c = 100 - TAMANHO_PADRAO; }
  return { x: a, y: c, x2: b, y2: d };
}

export default function DamagePhotoMarker({ fotos, onChange, readOnly }) {
  const { t } = useTextos();
  const inputRef = useRef(null);
  // Um arrasto de verdade termina disparando "click" tambem. Sem lembrar
  // disso, desenhar por cima de uma marcacao existente criaria a nova e, no
  // mesmo gesto, abriria a edicao da antiga.
  const arrastouRef = useRef(false);
  // Id do ponteiro capturado, ou null. A captura so comeca quando o gesto
  // VIRA arrasto -- nunca no pointerdown. Capturar cedo faz o navegador
  // entregar o "click" ao quadro em vez da forma, e ai tocar numa marcacao
  // existente deixaria de abrir a edicao enquanto uma ferramenta de desenho
  // estivesse selecionada.
  const capturaRef = useRef(null);
  const [draft, setDraft] = useState(null);

  // Ferramenta ativa e estilo escolhido. "pin" é o padrão -- quem não quiser
  // saber de desenho usa a tela exatamente como usava antes.
  const [ferramenta, setFerramenta] = useState(FORMAS.PIN);
  const [cor, setCor] = useState(null); // null = automática, pela gravidade
  const [espessura, setEspessura] = useState(ESPESSURAS[1].valor);

  // Traço em andamento (enquanto o dedo/mouse está pressionado).
  const [tracando, setTracando] = useState(null);

  // ── REPOSICIONAMENTO ─────────────────────────────────────────────────────
  // Marcar por toque nunca acerta de primeira: o dedo cobre justamente o
  // ponto que se quer apontar. Sem poder ajustar depois, o marcador fica
  // perto da avaria em vez de SOBRE ela.
  // { fotoId, id, modo: "todo" | "p1" | "p2", inicio:{x,y}, dx, dy }
  const [movendo, setMovendo] = useState(null);

  // AUD-CK1: a marcacao de TEXTO em edicao, escrita direto sobre a foto.
  // { fotoId, id }
  const [editandoTexto, setEditandoTexto] = useState(null);

  // AUD-CK1: a marca recem-inserida fica SELECIONADA. E o que permite
  // desfazer um toque involuntario num toque so, sem cobrar uma confirmacao
  // de quem acertou.
  const [selecionado, setSelecionado] = useState(null);

  const espacoDisponivel = MAX_FOTOS_AVARIA - fotos.length;
  const usaArraste = FORMAS_COM_ARRASTE.indexOf(ferramenta) >= 0;

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    const aceitas = files.slice(0, espacoDisponivel);
    e.target.value = "";
    const novas = [];
    for (const file of aceitas) {
      try {
        const dataUrl = await compressImageFile(file);
        novas.push({ id: generateId("avf"), dataUrl, pontos: [] });
      } catch (err) {
        console.error("Falha ao comprimir foto de avaria:", err);
      }
    }
    if (novas.length > 0) onChange([...fotos, ...novas]);
  }

  function removerFoto(fotoId) {
    onChange(fotos.filter((f) => f.id !== fotoId));
    if (draft && draft.fotoId === fotoId) setDraft(null);
    setTracando(null);
  }

  // Converte a posição do ponteiro em porcentagem da imagem, já limitada a
  // 0-100: arrastar para fora da foto encosta na borda em vez de gravar
  // coordenada negativa, que depois desenharia fora do quadro.
  function posPercentual(e, elemento) {
    const rect = elemento.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return {
      x: Math.min(100, Math.max(0, x)),
      y: Math.min(100, Math.max(0, y)),
    };
  }

  const limitar = (v) => Math.min(100, Math.max(0, v));

  // Desloca uma marcação. Função pura: devolve cópia, não altera o registro.
  // "todo" move a marcação inteira; "p1"/"p2" movem só uma ponta, que é o
  // que permite apontar a seta exatamente no risco sem refazer o traço.
  function aplicarDelta(p, dx, dy, modo) {
    // AUD-CK1: arrastar a alca colorida do texto muda o TAMANHO DA LETRA,
    // ao vivo. dx/dy vem em % da imagem; 10% de arrasto mexe ~5 pontos.
    // O teto de 40 vale aqui e nos botoes A+/A-, para nao existirem dois
    // limites diferentes para a mesma coisa.
    if (modo === "tamanho") {
      const base = typeof p.espessura === "number" ? p.espessura : 4;
      return { ...p, espessura: Math.max(1, Math.min(40, Math.round(base + (dx + dy) / 2))) };
    }
    if (modo === "p1") return { ...p, x: limitar(p.x + dx), y: limitar(p.y + dy) };
    if (modo === "p2") {
      return {
        ...p,
        x2: limitar((typeof p.x2 === "number" ? p.x2 : p.x) + dx),
        y2: limitar((typeof p.y2 === "number" ? p.y2 : p.y) + dy),
      };
    }
    return {
      ...p,
      x: limitar(p.x + dx),
      y: limitar(p.y + dy),
      x2: typeof p.x2 === "number" ? limitar(p.x2 + dx) : p.x2,
      y2: typeof p.y2 === "number" ? limitar(p.y2 + dy) : p.y2,
    };
  }

  // Posição a DESENHAR durante o arrasto. Nada é gravado enquanto o dedo
  // está na tela -- a gravação acontece uma vez só, ao soltar. Sem isto,
  // cada pixel de movimento viraria uma escrita no rascunho.
  function pontoExibido(fotoId, p) {
    if (!movendo || movendo.fotoId !== fotoId || movendo.id !== p.id) return p;
    return aplicarDelta(normalizarPonto(p), movendo.dx, movendo.dy, movendo.modo);
  }

  function iniciarMover(e, fotoId, ponto, modo) {
    if (readOnly || draft) return;
    // Impede que o mesmo gesto comece a desenhar uma marcação nova.
    e.stopPropagation();
    const quadro = e.currentTarget.closest(".damage-photo__frame");
    if (!quadro) return;
    arrastouRef.current = false;
    capturaRef.current = null;
    setTracando(null);
    setMovendo({ fotoId, id: ponto.id, modo, inicio: posPercentual(e, quadro), dx: 0, dy: 0 });
  }

  // A captura só começa quando o gesto vira arrasto -- nunca no pointerdown.
  // Capturar cedo faz o navegador entregar o "click" ao quadro em vez da
  // marcação, e aí tocar numa marcação deixaria de abrir a edição.
  function capturar(e) {
    if (capturaRef.current !== null) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
      capturaRef.current = e.pointerId;
    } catch (err) {
      /* navegador sem suporte: o gesto ainda funciona dentro da foto */
    }
  }

  function liberarCaptura(e) {
    if (capturaRef.current === null) return;
    try {
      e.currentTarget.releasePointerCapture(capturaRef.current);
    } catch (err) {
      /* nada a liberar */
    }
    capturaRef.current = null;
  }

  function novoDraft(fotoId, x, y, x2, y2) {
    return {
      fotoId,
      id: null,
      forma: ferramenta,
      x,
      y,
      x2: typeof x2 === "number" ? x2 : null,
      y2: typeof y2 === "number" ? y2 : null,
      cor,
      espessura,
      texto: "",
      tipo: TIPOS_AVARIA[0],
      gravidade: GRAVIDADE_AVARIA.LEVE,
      observacao: "",
    };
  }

  // AUD-CK1: grava a marca direto, sem passar pelo modal, e a deixa
  // selecionada. O modal continua existindo para EDITAR (toque na marca).
  function inserirPonto(fotoId, dados) {
    const id = generateId("avp");
    onChange(
      fotos.map((f) => (f.id !== fotoId ? f : { ...f, pontos: [...f.pontos, { ...dados, id }] }))
    );
    setSelecionado({ fotoId, id });
    return id;
  }

  // AUD-CK1: a criacao acontece AQUI, num botao, e nao no toque sobre a
  // foto. A marcacao nasce no CENTRO e ja selecionada -- o usuario arrasta
  // ate o lugar, que e o gesto que ele consegue fazer com precisao.
  function inserirNoCentro(fotoId) {
    if (readOnly || draft) return;
    // AUD-CK1: a segunda marcacao NAO nasce em cima da primeira.
    //
    // Nascer sempre em 50/50 parecia razoavel ate o uso real: inserir dois
    // textos seguidos dava a impressao de que o botao nao funcionava, porque
    // o novo ficava exatamente atras do anterior. Com simbolos o problema
    // existe igual, so e menos visivel porque eles tem tamanho.
    //
    // O escalonamento e em grade, por contagem: previsivel, sem aleatorio
    // (aleatorio tornaria o comportamento impossivel de testar e de prever
    // para quem usa).
    // A sequencia alterna EM TORNO do centro: a primeira marcacao continua
    // nascendo em 50/50 (e o lugar que o usuario espera), e so as seguintes
    // se afastam, para um lado e para o outro. A primeira versao desta conta
    // comecava na borda da grade e empurrava ate a PRIMEIRA marcacao para
    // fora do centro -- onze assertions cairam de uma vez e estavam certas.
    const jaTem = (fotos.find((f) => f.id === fotoId) || { pontos: [] }).pontos.length;
    const ESCADA = [0, 1, -1, 2, -2, 3, -3];
    const px = limitarPercentual(50 + ESCADA[jaTem % ESCADA.length] * 9);
    const py = limitarPercentual(50 + ESCADA[Math.floor(jaTem / ESCADA.length) % ESCADA.length] * 9);
    const base = novoDraft(fotoId, px, py);
    delete base.fotoId;
    delete base.id;
    const id = inserirPonto(fotoId, { ...base, ...(usaArraste ? geometriaPadrao(ferramenta, px, py) : {}) });
    // AUD-CK1: o TEXTO nasce junto com o campo de escrita SOBRE a foto. Era
    // o modal que atrapalhava -- escrever deve acontecer onde o texto vai
    // ficar, e nao noutra tela.
    if (ferramenta === FORMAS.TEXTO) setEditandoTexto({ fotoId, id });
  }

  // AUD-CK1: altera UM campo de UMA marcacao, sem passar pelo modal. E o
  // que permite escrever o texto e mudar o tamanho da letra ao vivo.
  function atualizarPonto(fotoId, id, mudanca) {
    onChange(
      fotos.map((f) =>
        f.id !== fotoId
          ? f
          : { ...f, pontos: f.pontos.map((p) => (p.id === id ? { ...normalizarPonto(p), ...mudanca } : p)) }
      )
    );
  }

  function pontoSelecionado() {
    if (!selecionado) return null;
    const f = fotos.find((x) => x.id === selecionado.fotoId);
    const p = f && f.pontos.find((x) => x.id === selecionado.id);
    return p ? normalizarPonto(p) : null;
  }

  // Tamanho da letra = espessura. Nao e gambiarra: a tela desenha o texto
  // com font-size 11 + espessura*2, e o PDF (exportChecklist) usa a MESMA
  // formula. Mexer na espessura muda os dois juntos, sem campo novo no
  // modelo de dados e sem tocar no export.
  function mudarTamanhoTexto(delta) {
    const p = pontoSelecionado();
    if (!p || !selecionado) return;
    const novo = Math.max(1, Math.min(40, (p.espessura || 4) + delta));
    atualizarPonto(selecionado.fotoId, selecionado.id, { espessura: novo });
  }

  function removerSelecionado() {
    if (!selecionado) return;
    onChange(
      fotos.map((f) =>
        f.id !== selecionado.fotoId
          ? f
          : { ...f, pontos: f.pontos.filter((p) => p.id !== selecionado.id) }
      )
    );
    setSelecionado(null);
    setEditandoTexto(null);
  }

  function editarSelecionado() {
    if (!selecionado) return;
    const foto = fotos.find((f) => f.id === selecionado.fotoId);
    const ponto = foto && foto.pontos.find((p) => p.id === selecionado.id);
    if (ponto) abrirEdicao(selecionado.fotoId, ponto);
  }

  function onPointerDown(e, fotoId) {
    if (readOnly || draft) return;
    arrastouRef.current = false;
    capturaRef.current = null;
    // AUD-CK1 -- NO DEDO, arrastar sobre a foto nao cria nada.
    //
    // O limiar de desenho e 2% da imagem. Num celular isso da uns 7 pixels
    // -- MENOS que o tremor normal de um toque. Na pratica, um "toque" que
    // escorregava virava um retangulo que ninguem pediu, e parecia que as
    // formas se comportavam de jeitos diferentes. Nao era isso: era o
    // limiar menor que a precisao do dedo.
    //
    // Com mouse ou caneta, onde a precisao existe, desenhar arrastando
    // continua funcionando exatamente como sempre funcionou.
    if (!usaArraste || e.pointerType === "touch") return;
    const p = posPercentual(e, e.currentTarget);
    setTracando({ fotoId, x: p.x, y: p.y, x2: p.x, y2: p.y });
  }

  function onPointerMove(e) {
    const p = posPercentual(e, e.currentTarget);

    if (movendo) {
      const dx = p.x - movendo.inicio.x;
      const dy = p.y - movendo.inicio.y;
      // Limiar menor que o do desenho: reposicionar é ajuste fino, e quem
      // corrige um pino mexe poucos pixels.
      if (!arrastouRef.current && Math.abs(dx) < 0.8 && Math.abs(dy) < 0.8) return;
      arrastouRef.current = true;
      capturar(e);
      setMovendo((mv) => (mv ? { ...mv, dx, dy } : mv));
      return;
    }

    if (!tracando || !usaArraste) return;
    // Passou de toque para arrasto: agora sim capturar, para o traço não
    // "morrer" se o dedo sair da foto no meio do caminho.
    if (Math.abs(p.x - tracando.x) >= 2 || Math.abs(p.y - tracando.y) >= 2) capturar(e);
    setTracando((tr) => (tr ? { ...tr, x2: p.x, y2: p.y } : tr));
  }

  function onPointerUp(e) {
    liberarCaptura(e);

    if (movendo) {
      const mv = movendo;
      setMovendo(null);
      // Sem deslocamento real foi um toque, não um arrasto: nada é gravado e
      // o "click" da marcação abre a edição sozinho.
      if (!arrastouRef.current) return;
      onChange(
        fotos.map((f) =>
          f.id !== mv.fotoId
            ? f
            : {
                ...f,
                pontos: f.pontos.map((p) =>
                  p.id === mv.id ? aplicarDelta(normalizarPonto(p), mv.dx, mv.dy, mv.modo) : p
                ),
              }
        )
      );
      return;
    }

    if (!tracando) return;
    const tr = tracando;
    setTracando(null);

    // AUD-CK1 -- TOCAR NA FOTO NAO CRIA NADA.
    //
    // Criar e trabalho do botao "+ Inserir". Aqui so sobrevive o ARRASTO no
    // vazio, que desenha do tamanho exato -- o caminho que ja existia e que
    // continua util no mouse. Um toque parado nao insere, e e justamente
    // isso que acaba com a disputa entre "mover uma marcacao" e "criar
    // outra por baixo dela".
    const dx = Math.abs((tr.x2 ?? tr.x) - tr.x);
    const dy = Math.abs((tr.y2 ?? tr.y) - tr.y);
    if (!usaArraste || (dx < 2 && dy < 2)) return;
    arrastouRef.current = true;
    const base = novoDraft(tr.fotoId, tr.x, tr.y);
    delete base.fotoId;
    delete base.id;
    inserirPonto(tr.fotoId, { ...base, x2: tr.x2, y2: tr.y2 });
  }

  function onPointerCancel() {
    capturaRef.current = null;
    setTracando(null);
    setMovendo(null);
  }

  function abrirEdicao(fotoId, ponto) {
    if (readOnly) return;
    setTracando(null);
    const pn = normalizarPonto(ponto);
    // AUD-CK1: tocar numa marcacao de TEXTO abre a escrita SOBRE a foto, em
    // vez do modal. As outras formas continuam indo para o modal, onde
    // ficam tipo, gravidade e observacao.
    if (pn.forma === FORMAS.TEXTO) {
      setSelecionado({ fotoId, id: ponto.id });
      setEditandoTexto({ fotoId, id: ponto.id });
      return;
    }
    setDraft({ fotoId, ...pn });
  }

  function confirmar() {
    onChange(
      fotos.map((f) => {
        if (f.id !== draft.fotoId) return f;
        const { fotoId, ...dados } = draft;
        if (!draft.id) {
          return { ...f, pontos: [...f.pontos, { ...dados, id: generateId("avp") }] };
        }
        return { ...f, pontos: f.pontos.map((p) => (p.id === draft.id ? dados : p)) };
      })
    );
    setDraft(null);
  }

  function remover() {
    onChange(
      fotos.map((f) => (f.id === draft.fotoId ? { ...f, pontos: f.pontos.filter((p) => p.id !== draft.id) } : f))
    );
    setDraft(null);
  }

  // ── desenho de uma marcação na camada SVG ────────────────────────────────
  // AUD-CK1: `destacado` = esta marcacao esta selecionada AGORA. E o que
  // decide mostrar a caixa de selecao e as alcas de canto do texto. Sem
  // isso, ou toda marcacao mostraria alcas (poluindo a foto), ou nenhuma
  // mostraria (o caso anterior, em que nao dava para pegar).
  function desenharForma(bruto, chave, interativo, aoClicar, aoMover, destacado) {
    const p = normalizarPonto(bruto);
    if (p.forma === FORMAS.PIN) return null;
    const c = corDaMarcacao(p);
    const esp = p.espessura;
    const x = p.x + "%";
    const y = p.y + "%";
    const temFim = typeof p.x2 === "number" && typeof p.y2 === "number";

    const comuns = { fill: "none", stroke: c, strokeWidth: esp, strokeLinecap: "round" };
    // Traço transparente e gordo por baixo: dá área de toque de verdade numa
    // linha de 2px, que no dedo é impossível de acertar.
    const alvo = { fill: "none", stroke: "transparent", strokeWidth: Math.max(esp + 14, 18), strokeLinecap: "round" };

    let miolo = null;
    let area = null;
    // AUD-CK1: largura aproximada do texto, em px. Nao da para medir o
    // texto antes de desenhar sem ir ao DOM, e 0.62 do tamanho da fonte por
    // caractere e uma aproximacao boa para peso 700. Serve para dois fins:
    // dar ao texto uma AREA DE TOQUE de verdade e posicionar a alca de
    // tamanho logo depois da ultima letra.
    const larguraTexto = Math.max(1, (p.texto || "...").length) * (11 + esp * 2) * 0.62;
    const alturaTexto = (11 + esp * 2) * 1.5;

    if (p.forma === FORMAS.SETA && temFim) {
      const mid = "seta-" + chave;
      miolo = (
        <>
          <defs>
            <marker
              id={mid}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="4.5"
              markerHeight="4.5"
              orient="auto-start-reverse"
              markerUnits="strokeWidth"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
            </marker>
          </defs>
          <line x1={x} y1={y} x2={p.x2 + "%"} y2={p.y2 + "%"} markerEnd={`url(#${mid})`} {...comuns} />
        </>
      );
      area = <line x1={x} y1={y} x2={p.x2 + "%"} y2={p.y2 + "%"} {...alvo} />;
    } else if (p.forma === FORMAS.RETANGULO && temFim) {
      const rx = Math.min(p.x, p.x2) + "%";
      const ry = Math.min(p.y, p.y2) + "%";
      const rw = Math.abs(p.x2 - p.x) + "%";
      const rh = Math.abs(p.y2 - p.y) + "%";
      miolo = <rect x={rx} y={ry} width={rw} height={rh} {...comuns} />;
      area = <rect x={rx} y={ry} width={rw} height={rh} {...alvo} />;
    } else if (p.forma === FORMAS.ELIPSE && temFim) {
      const cx = (p.x + p.x2) / 2 + "%";
      const cy = (p.y + p.y2) / 2 + "%";
      const rx = Math.abs(p.x2 - p.x) / 2 + "%";
      const ry = Math.abs(p.y2 - p.y) / 2 + "%";
      miolo = <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...comuns} />;
      area = <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...alvo} />;
    } else if (p.forma === FORMAS.TEXTO) {
      // paintOrder="stroke" desenha o contorno branco ATRÁS do preenchimento:
      // o texto fica legível tanto em lataria clara quanto escura.
      miolo = (
        <text
          x={x}
          y={y}
          fill={c}
          stroke="#ffffff"
          strokeWidth={Math.max(esp, 3)}
          paintOrder="stroke"
          fontSize={11 + esp * 2}
          fontWeight="700"
          dominantBaseline="middle"
        >
          {p.texto || "…"}
        </text>
      );
      // AUD-CK1: a area de toque passa a ser um RETANGULO do tamanho do
      // texto, e nao o desenho das letras. Antes, pegar um texto com o dedo
      // exigia acertar o traco de uma letra.
      area = (
        <rect
          x={x}
          y={y}
          width={larguraTexto}
          height={alturaTexto}
          transform={`translate(0, ${-alturaTexto / 2})`}
          fill="transparent"
        />
      );
    }

    if (!miolo) return null;

    return (
      <g
        key={chave}
        style={{ pointerEvents: interativo ? "auto" : "none", cursor: interativo ? "move" : "default" }}
        onPointerDown={interativo && aoMover ? (e) => aoMover(e, "todo") : undefined}
        onClick={
          interativo
            ? (e) => {
                e.stopPropagation();
                if (arrastouRef.current) return;
                aoClicar();
              }
            : undefined
        }
      >
        {area}
        {miolo}
        {/* Alças das pontas. Mover a forma inteira mantém o traço; mover uma
            ponta é o que permite encostar a seta exatamente no risco sem
            precisar apagar e desenhar de novo. */}
        {/* AUD-CK1 -- caixa de selecao do TEXTO, com alcas NAS QUINAS.
            A primeira versao punha as alcas sobre o proprio texto: a de
            mover ficava debaixo das primeiras letras e a de tamanho encostada
            na ultima -- escondidas e dificeis de pegar. Agora ha uma caixa
            tracejada em volta, a alca BRANCA na quina superior esquerda
            (move) e a COLORIDA na inferior direita (estica a letra), as duas
            FORA do texto.
            So aparecem na marcacao selecionada: alcas em todas poluiriam a
            foto, e nenhuma era o defeito anterior. */}
        {interativo && aoMover && destacado && p.forma === FORMAS.TEXTO && (
          <>
            <rect
              x={x}
              y={y}
              width={larguraTexto}
              height={alturaTexto}
              transform={`translate(0, ${-alturaTexto / 2})`}
              fill="none"
              stroke={c}
              strokeWidth="1.5"
              strokeDasharray="5 4"
              pointerEvents="none"
            />
            <circle
              cx={x}
              cy={y}
              r="10"
              fill="#ffffff"
              stroke={c}
              strokeWidth="2.5"
              transform={`translate(0, ${-alturaTexto / 2})`}
              style={{ cursor: "move" }}
              onPointerDown={(e) => aoMover(e, "todo")}
            />
            <circle
              cx={x}
              cy={y}
              r="10"
              fill={c}
              stroke="#ffffff"
              strokeWidth="2.5"
              transform={`translate(${larguraTexto}, ${alturaTexto / 2})`}
              style={{ cursor: "nwse-resize" }}
              onPointerDown={(e) => aoMover(e, "tamanho")}
            />
          </>
        )}
        {interativo && aoMover && temFim && (
          <>
            <circle
              cx={x}
              cy={y}
              r="7"
              fill="#ffffff"
              stroke={c}
              strokeWidth="2"
              onPointerDown={(e) => aoMover(e, "p1")}
            />
            <circle
              cx={p.x2 + "%"}
              cy={p.y2 + "%"}
              r="7"
              fill="#ffffff"
              stroke={c}
              strokeWidth="2"
              onPointerDown={(e) => aoMover(e, "p2")}
            />
          </>
        )}
      </g>
    );
  }

  const botaoFerramenta = (valor, rotulo) => (
    <button
      key={valor}
      type="button"
      onClick={() => setFerramenta(valor)}
      aria-pressed={ferramenta === valor}
      style={{
        background: ferramenta === valor ? "#111827" : "transparent",
        border: "1px solid " + (ferramenta === valor ? "#111827" : "#d1d5db"),
        borderRadius: 6,
        color: ferramenta === valor ? "#ffffff" : "#374151",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 600,
        padding: "6px 10px",
      }}
    >
      {rotulo}
    </button>
  );

  return (
    <div className="damage-photo">
      {fotos.length === 0 && readOnly && <p className="empty-state">{t("checklist.dmg_nenhuma_foto")}</p>}

      {!readOnly && fotos.length > 0 && (
        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {botaoFerramenta(FORMAS.PIN, t("checklist.dmg_fer_pino", "Pino"))}
            {botaoFerramenta(FORMAS.SETA, t("checklist.dmg_fer_seta", "Seta"))}
            {botaoFerramenta(FORMAS.RETANGULO, t("checklist.dmg_fer_retangulo", "Retângulo"))}
            {botaoFerramenta(FORMAS.ELIPSE, t("checklist.dmg_fer_elipse", "Elipse"))}
            {botaoFerramenta(FORMAS.TEXTO, t("checklist.dmg_fer_texto", "Texto"))}
          </div>

          <span style={{ background: "#e5e7eb", height: 20, width: 1 }} />

          <div style={{ alignItems: "center", display: "flex", gap: 4 }}>
            {CORES_MARCACAO.map((c) => (
              <button
                key={c.valor || "auto"}
                type="button"
                onClick={() => setCor(c.valor)}
                title={c.rotulo}
                aria-label={c.rotulo}
                aria-pressed={cor === c.valor}
                style={{
                  background: c.valor || "linear-gradient(135deg,#16a34a 0 33%,#ea580c 33% 66%,#dc2626 66% 100%)",
                  border: "2px solid " + (cor === c.valor ? "#111827" : "#d1d5db"),
                  borderRadius: "50%",
                  cursor: "pointer",
                  height: 22,
                  padding: 0,
                  width: 22,
                }}
              />
            ))}
          </div>

          <span style={{ background: "#e5e7eb", height: 20, width: 1 }} />

          <div style={{ display: "flex", gap: 4 }}>
            {ESPESSURAS.map((e) => (
              <button
                key={e.valor}
                type="button"
                onClick={() => setEspessura(e.valor)}
                aria-pressed={espessura === e.valor}
                title={e.rotulo}
                style={{
                  alignItems: "center",
                  background: espessura === e.valor ? "#f3f4f6" : "transparent",
                  border: "1px solid " + (espessura === e.valor ? "#111827" : "#d1d5db"),
                  borderRadius: 6,
                  cursor: "pointer",
                  display: "flex",
                  height: 28,
                  justifyContent: "center",
                  padding: 0,
                  width: 34,
                }}
              >
                <span style={{ background: "#374151", borderRadius: 99, height: e.valor, width: 18 }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dica UNICA, igual para todas as ferramentas. Antes ela mudava de
          texto conforme a ferramenta escolhida, e como o texto novo era mais
          longo a foto DESCIA na tela toda vez que o mecanico trocava de
          ferramenta -- ele mirava num ponto e a imagem escapava debaixo do
          dedo. Texto fixo resolve na origem, sem reservar altura no escuro. */}
      {!readOnly && fotos.length > 0 && (
        <p className="hint" style={{ marginTop: -4 }}>
          {t(
            "checklist.dmg_dica",
            "Toque para marcar \u00b7 arraste para desenhar \u00b7 arraste uma marca\u00e7\u00e3o para mov\u00ea-la \u00b7 toque nela para editar"
          )}
        </p>
      )}

      {(fotos.length > 0 || !readOnly) && (
        <div className="damage-photo__grid">
          {fotos.map((foto) => {
            // Numero do pino conta SO os pinos, nunca a posicao no array. E o
            // mesmo criterio do documento em PDF (rotuloMarcacao no
            // exportChecklist): se contasse tudo, uma foto com setas mostraria
            // "8" na tela e "2" no PDF para o mesmo pino.
            let seqPin = 0;
            const numeroDoPino = {};
            foto.pontos.forEach((p) => {
              if (normalizarPonto(p).forma === FORMAS.PIN) {
                seqPin += 1;
                numeroDoPino[p.id] = seqPin;
              }
            });
            return (
            <div className="damage-photo__item" key={foto.id}>
              <div
                className="damage-photo__frame"
                style={{
                  position: "relative",
                  // AUD-CK1: a foto nao rola a pagina enquanto for editavel.
                  //
                  // Antes isto valia SO para as ferramentas de arrasto, e o
                  // efeito era que, com pino ou texto escolhidos, arrastar
                  // uma marcacao virava rolagem de pagina: o navegador
                  // assumia o gesto e mandava pointercancel. O pino escapava
                  // por ter touchAction proprio no <button>; as formas em
                  // SVG nao tinham, e por isso nao se moviam no dedo.
                  //
                  // O custo e assumido: para rolar a pagina, o dedo vai
                  // fora da foto. E o comportamento de qualquer ferramenta
                  // de anotacao em imagem. Em leitura, a foto rola normal.
                  touchAction: readOnly ? "manipulation" : "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                }}
                onDragStart={(e) => e.preventDefault()}
                onPointerDown={(e) => onPointerDown(e, foto.id)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
              >
                {/* draggable={false} e obrigatorio: o navegador arrasta imagem
                    por padrao. Ao pressionar e mover sobre a foto no mouse, o
                    Chrome inicia o arrasto nativo da imagem e dispara
                    pointercancel -- o traco morre antes de nascer. Com
                    pointerEvents "none" os eventos vao direto para o quadro. */}
                <img
                  src={foto.dataUrl}
                  alt={t("checklist.dmg_alt_foto_avaria", "Foto de avaria do veículo")}
                  draggable={false}
                  style={{ pointerEvents: "none" }}
                />

                <svg
                  style={{
                    height: "100%",
                    left: 0,
                    pointerEvents: "none",
                    position: "absolute",
                    top: 0,
                    width: "100%",
                  }}
                >
                  {foto.pontos.map((p) =>
                    desenharForma(
                      pontoExibido(foto.id, p),
                      foto.id + "-" + p.id,
                      // AUD-CK1: a marcacao em EDICAO de texto fica inerte --
                      // sem alcas e sem capturar toque -- para nao disputar
                      // com o campo de digitacao que esta por cima dela.
                      !readOnly && !(editandoTexto && editandoTexto.id === p.id),
                      () => abrirEdicao(foto.id, p),
                      (e, modo) => iniciarMover(e, foto.id, p, modo),
                      // AUD-CK1: as alcas de canto so na marcacao selecionada.
                      !!selecionado && selecionado.id === p.id
                    )
                  )}
                  {tracando &&
                    tracando.fotoId === foto.id &&
                    usaArraste &&
                    desenharForma(
                      {
                        ...tracando,
                        id: "previa",
                        forma: ferramenta,
                        cor,
                        espessura,
                        gravidade: GRAVIDADE_AVARIA.LEVE,
                      },
                      foto.id + "-previa",
                      false,
                      null
                    )}
                </svg>

                {foto.pontos.map((p) => {
                  const pn = normalizarPonto(p);
                  if (pn.forma !== FORMAS.PIN) return null;
                  const pe = pontoExibido(foto.id, p);
                  return (
                    <button
                      type="button"
                      key={p.id}
                      className="vehicle-diagram__marker"
                      style={{
                        left: `${pe.x}%`,
                        top: `${pe.y}%`,
                        background: corDaMarcacao(pn),
                        cursor: readOnly ? "default" : "move",
                        touchAction: "none",
                      }}
                      title={`${p.tipo} (${GRAVIDADE_LABELS[p.gravidade]})${p.observacao ? " — " + p.observacao : ""}`}
                      onPointerDown={(e) => iniciarMover(e, foto.id, p, "todo")}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Arrastou: já reposicionou, não abre a edição também.
                        if (arrastouRef.current) return;
                        abrirEdicao(foto.id, p);
                      }}
                    >
                      {numeroDoPino[p.id]}
                    </button>
                  );
                })}

                {/* AUD-CK1 -- escrever o texto SOBRE a foto, no lugar onde
                    ele vai ficar. Fica DENTRO do quadro, posicionado em % na
                    mesma coordenada da marcacao, e com a mesma cor e o mesmo
                    tamanho que o texto desenhado tera -- o que se digita e o
                    que se ve. Fechar e explicito (Enter, Esc ou o botao
                    Pronto): fechar no blur faria o campo sumir toda vez que o
                    dedo encostasse em A+ ou numa cor. */}
                {!readOnly && editandoTexto && editandoTexto.fotoId === foto.id &&
                  (() => {
                    const alvo = foto.pontos.find((pp) => pp.id === editandoTexto.id);
                    if (!alvo) return null;
                    const pn = normalizarPonto(alvo);
                    return (
                      <input
                        autoFocus
                        className="damage-photo__texto-inline"
                        value={pn.texto}
                        placeholder={t("checklist.dmg_texto_ph", "Escreva aqui")}
                        onChange={(ev) => atualizarPonto(foto.id, alvo.id, { texto: ev.target.value })}
                        onPointerDown={(ev) => ev.stopPropagation()}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === "Escape") setEditandoTexto(null);
                        }}
                        style={{
                          background: "rgba(255,255,255,0.94)",
                          border: "2px solid " + corDaMarcacao(pn),
                          borderRadius: 6,
                          color: corDaMarcacao(pn),
                          fontSize: 11 + pn.espessura * 2,
                          fontWeight: 700,
                          left: pn.x + "%",
                          // AUD-CK1: a largura acompanha o conteudo. Com
                          // 88% fixos, o campo nascia no ponto da marcacao e
                          // se esticava ate fora da foto -- parecia deslocado
                          // mesmo estando na coordenada certa.
                          maxWidth: "70%",
                          width: Math.max(8, (pn.texto || "").length + 4) + "ch",
                          outline: "none",
                          padding: "2px 6px",
                          position: "absolute",
                          top: pn.y + "%",
                          transform: "translate(-4px, -50%)",
                        }}
                      />
                    );
                  })()}

                {!readOnly && (
                  <button
                    type="button"
                    className="damage-photo__remove"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      removerFoto(foto.id);
                    }}
                    aria-label={t("checklist.photo_remove_aria")}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* AUD-CK1 -- barra da marca selecionada.
                  Fica ABAIXO da foto, e nao como um "x" em cima da marca, por
                  dois motivos medidos nesta tela: um alvo pequeno sobre a
                  imagem disputa o gesto de arrastar (que e justamente como se
                  posiciona a marca), e o pino e HTML enquanto as outras formas
                  sao SVG -- um botao sobreposto teria de ser feito duas vezes,
                  de dois jeitos. Aqui e um lugar so, com alvo grande. */}
              {!readOnly && (
                <div className="damage-photo__acoes" style={{
                  display: "flex", gap: 8, marginTop: 6,
                }}>
                  <button
                    type="button"
                    className="btn btn--primary"
                    style={{ flex: 1 }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); inserirNoCentro(foto.id); }}
                  >
                    {t("checklist.dmg_inserir", "+ Inserir na foto")}
                  </button>
                </div>
              )}

              {!readOnly && selecionado && selecionado.fotoId === foto.id && (
                <div className="damage-photo__selecao" style={{
                  alignItems: "center", display: "flex", gap: 8, marginTop: 6,
                }}>
                  <span style={{ color: "#6b7280", flex: 1, fontSize: 12 }}>
                    {t("checklist.dmg_marcacao_selecionada", "Marcação selecionada")}
                  </span>
                  <button type="button" className="btn btn--ghost" onClick={editarSelecionado}>
                    {t("checklist.dmg_editar", "Editar")}
                  </button>
                  <button type="button" className="btn btn--secondary" onClick={removerSelecionado}>
                    {t("checklist.dmg_remover", "Remover")}
                  </button>
                </div>
              )}

              {/* AUD-CK1 -- controles do TEXTO, so quando um texto esta
                  selecionado. Tamanho e cor ficam aqui, junto da foto, em vez
                  de noutra tela: e a diferenca entre ajustar olhando o
                  resultado e ajustar no escuro. */}
              {!readOnly && selecionado && selecionado.fotoId === foto.id &&
                (pontoSelecionado() || {}).forma === FORMAS.TEXTO && (
                <div className="damage-photo__texto-barra" style={{
                  alignItems: "center", display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6,
                }}>
                  <button type="button" className="btn btn--ghost"
                          onClick={() => mudarTamanhoTexto(-1)}
                          aria-label={t("checklist.dmg_letra_menor", "Letra menor")}>
                    A&minus;
                  </button>
                  <button type="button" className="btn btn--ghost"
                          onClick={() => mudarTamanhoTexto(1)}
                          aria-label={t("checklist.dmg_letra_maior", "Letra maior")}>
                    A+
                  </button>
                  {CORES_MARCACAO.map((cc) => (
                    <button
                      key={String(cc.valor)}
                      type="button"
                      aria-label={cc.rotulo}
                      onClick={() => atualizarPonto(selecionado.fotoId, selecionado.id, { cor: cc.valor })}
                      style={{
                        background: cc.valor || "linear-gradient(135deg,#22c55e,#eab308,#dc2626)",
                        border: "2px solid " + ((pontoSelecionado() || {}).cor === cc.valor ? "#111827" : "#d1d5db"),
                        borderRadius: "50%", cursor: "pointer", height: 24, padding: 0, width: 24,
                      }}
                    />
                  ))}
                  {editandoTexto && editandoTexto.id === selecionado.id && (
                    <button type="button" className="btn btn--primary"
                            onClick={() => setEditandoTexto(null)}>
                      {t("checklist.dmg_pronto", "Pronto")}
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}

          {!readOnly && espacoDisponivel > 0 && (
            <button type="button" className="damage-photo__add" onClick={() => inputRef.current?.click()}>
              <span>📷</span>
              <span>{t("checklist.dmg_adicionar_foto")}</span>
            </button>
          )}
        </div>
      )}

      {!readOnly && (
        <p className="hint">
          {t("checklist.dmg_count_hint", { count: fotos.length, max: MAX_FOTOS_AVARIA })}
          {espacoDisponivel === 0 && t("checklist.dmg_limite_atingido")}
        </p>
      )}

      {!readOnly && (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          style={{ display: "none" }}
          onChange={handleFiles}
        />
      )}

      {draft && (
        <div className="vehicle-diagram__point-form">
          {draft.forma === FORMAS.TEXTO && (
            <label>
              {t("checklist.dmg_texto_campo", "Texto sobre a foto")}
              <input
                value={draft.texto}
                autoFocus
                maxLength={60}
                onChange={(e) => setDraft({ ...draft, texto: e.target.value })}
                placeholder={t("checklist.dmg_texto_placeholder", "Ex.: amassado profundo")}
              />
            </label>
          )}

          <div className="form-grid">
            <label>
              {t("checklist.dmg_tipo_avaria")}
              <select value={draft.tipo} onChange={(e) => setDraft({ ...draft, tipo: e.target.value })}>
                {TIPOS_AVARIA.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("checklist.dmg_gravidade")}
              <select value={draft.gravidade} onChange={(e) => setDraft({ ...draft, gravidade: e.target.value })}>
                {Object.entries(GRAVIDADE_LABELS).map(([valor, label]) => (
                  <option key={valor} value={valor}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            {t("checklist.dmg_observacao")}
            <input
              value={draft.observacao}
              onChange={(e) => setDraft({ ...draft, observacao: e.target.value })}
              placeholder={t("checklist.dmg_observacao_placeholder")}
            />
          </label>

          <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
            <span style={{ color: "#6b7280", fontSize: 12 }}>{t("checklist.dmg_cor", "Cor")}</span>
            {CORES_MARCACAO.map((c) => (
              <button
                key={c.valor || "auto"}
                type="button"
                onClick={() => setDraft({ ...draft, cor: c.valor })}
                title={c.rotulo}
                aria-label={c.rotulo}
                aria-pressed={draft.cor === c.valor}
                style={{
                  background: c.valor || "linear-gradient(135deg,#16a34a 0 33%,#ea580c 33% 66%,#dc2626 66% 100%)",
                  border: "2px solid " + (draft.cor === c.valor ? "#111827" : "#d1d5db"),
                  borderRadius: "50%",
                  cursor: "pointer",
                  height: 22,
                  padding: 0,
                  width: 22,
                }}
              />
            ))}
          </div>

          {draft.forma !== FORMAS.PIN && (
            <div style={{ alignItems: "center", display: "flex", gap: 8, marginTop: 8 }}>
              <span style={{ color: "#6b7280", fontSize: 12 }}>{t("checklist.dmg_espessura", "Espessura")}</span>
              {ESPESSURAS.map((e) => (
                <button
                  key={e.valor}
                  type="button"
                  onClick={() => setDraft({ ...draft, espessura: e.valor })}
                  aria-pressed={draft.espessura === e.valor}
                  title={e.rotulo}
                  style={{
                    alignItems: "center",
                    background: draft.espessura === e.valor ? "#f3f4f6" : "transparent",
                    border: "1px solid " + (draft.espessura === e.valor ? "#111827" : "#d1d5db"),
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "flex",
                    height: 28,
                    justifyContent: "center",
                    padding: 0,
                    width: 34,
                  }}
                >
                  <span style={{ background: "#374151", borderRadius: 99, height: e.valor, width: 18 }} />
                </button>
              ))}
            </div>
          )}

          <div className="vehicle-diagram__point-form-actions">
            <button type="button" className="btn btn--ghost" onClick={() => setDraft(null)}>
              {t("common.cancelar")}
            </button>
            {draft.id && (
              <button type="button" className="btn btn--secondary" onClick={remover}>
                {t("checklist.dmg_remover_ponto")}
              </button>
            )}
            <button type="button" className="btn btn--primary" onClick={confirmar}>
              {draft.id ? t("common.salvar") : t("checklist.dmg_adicionar_ponto")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
